// The server's case enums in the words staff read. A value the maps do not
// know - a template's free-text stage, an older row - is shown as written.

import type { CaseTeamRole, DocumentTypeCode } from "@duralex/shared";
import { SEP } from "../components/chrome/SepList";
import { practiceAreaLabel } from "../data/practice-areas";
import type { Matter } from "../data/types";

export const CASE_TYPE_LABEL: Record<string, string> = {
  asylum: "Asylum",
  withholding: "Withholding of removal",
  cat: "CAT protection",
  cancellationOfRemoval: "Cancellation of removal",
  adjustmentOfStatus: "Adjustment of status",
  naturalization: "Naturalization",
  familyPetition: "Family petition",
  employmentBased: "Employment-based",
  tps: "TPS",
  daca: "DACA",
  uVisa: "U visa",
  tVisa: "T visa",
  vawa: "VAWA",
  sij: "SIJ",
  bondHearing: "Bond hearing",
  autoAccident: "Auto accident",
  slipAndFall: "Slip and fall",
  medicalMalpractice: "Medical malpractice",
  workersComp: "Workers' compensation",
  wrongfulDeath: "Wrongful death",
  misdemeanor: "Misdemeanor",
  felony: "Felony",
  dui: "DUI",
  domesticViolence: "Domestic violence",
  drugOffense: "Drug offense",
  other: "Other",
};

export const STAGE_LABEL: Record<string, string> = {
  intake: "Intake",
  documentGathering: "Gathering documents",
  filing: "Filing",
  receiptNotice: "Receipt notice",
  biometrics: "Biometrics",
  interviewScheduled: "Interview scheduled",
  interviewCompleted: "Interview completed",
  decision: "Decision",
  appeal: "Appeal",
  masterHearing: "Master hearing",
  individualHearing: "Individual hearing",
  motionToReopen: "Motion to reopen",
  closedGranted: "Closed - granted",
  closedDenied: "Closed - denied",
  closedAdminClosed: "Closed - administratively",
  closedWithdrawn: "Closed - withdrawn",
  litigation: "Litigation",
  settlement: "Settlement",
  trial: "Trial",
  other: "Other",
};

export const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  pending: "Pending",
  onHold: "On hold",
  closed: "Closed",
  archived: "Archived",
};

export const TEAM_ROLE_LABEL: Record<CaseTeamRole, string> = {
  attorney: "Attorney",
  paralegal: "Paralegal",
  interpreter: "Interpreter",
  expert: "Expert",
  other: "Other",
};

// Keyed by the shared union, so a new server code fails the typecheck until named.
export const DOCUMENT_TYPE_LABEL: Record<DocumentTypeCode, string> = {
  nta: "Notice to appear",
  "hearing-notice": "Hearing notice",
  passport: "Passport",
  i94: "I-94 arrival record",
  i589: "I-589 asylum application",
  i130: "I-130 relative petition",
  i485: "I-485 adjustment of status",
  i765: "I-765 work permit",
  i131: "I-131 travel document",
  i864: "I-864 affidavit of support",
  n400: "N-400 naturalization",
  eoir28: "EOIR-28 entry of appearance",
  eoir33: "EOIR-33 change of address",
  eoir42b: "EOIR-42B cancellation",
  "uscis-receipt": "USCIS receipt",
  "uscis-approval": "USCIS approval",
  "uscis-denial": "USCIS denial",
  "uscis-rfe": "USCIS RFE",
  "uscis-noid": "USCIS NOID",
  "uscis-interview-notice": "USCIS interview notice",
  "ij-decision": "IJ decision",
  "bia-decision": "BIA decision",
  "police-report": "Police report",
  "medical-record": "Medical record",
  "court-filing": "Court filing",
  "country-conditions": "Country conditions",
  "legal-research": "Legal research",
  correspondence: "Correspondence",
  "birth-certificate": "Birth certificate",
  "marriage-certificate": "Marriage certificate",
  retainer: "Retainer agreement",
  other: "Other",
};

type Raw = string | null | undefined;

export const caseTypeLabel = (v: Raw): string => (v ? (CASE_TYPE_LABEL[v] ?? v) : "");
export const stageLabel = (v: Raw): string => (v ? (STAGE_LABEL[v] ?? v) : "");
export const statusLabel = (v: Raw): string => (v ? (STATUS_LABEL[v] ?? v) : "");
export const teamRoleLabel = (v: Raw): string =>
  v ? ((TEAM_ROLE_LABEL as Record<string, string>)[v] ?? v) : "";
export const documentTypeLabel = (v: Raw): string =>
  v ? ((DOCUMENT_TYPE_LABEL as Record<string, string>)[v] ?? v) : "";

/** An A-number reads with its prefix once, however the row spelled it. */
export const aNumberLabel = (v: string): string => (/^a/i.test(v) ? v : `A${v}`);

// A case type that only repeats its area ("Other") is not said twice.
export const caseKindLabel = (m: Pick<Matter, "practiceArea" | "subtype">): string => {
  const kind = caseTypeLabel(m.subtype);
  return kind === practiceAreaLabel(m.practiceArea) ? "" : kind;
};
export const matterKindLabel = (m: Pick<Matter, "practiceArea" | "subtype">): string => {
  const kind = caseKindLabel(m);
  return kind ? practiceAreaLabel(m.practiceArea) + SEP + kind : practiceAreaLabel(m.practiceArea);
};

export const clientNameLabel = (m: Pick<Matter, "clientName">): string =>
  m.clientName ?? "Unnamed client";
